/**
 * Fetch Wrapper with Automatic Token Refresh
 * 
 * This wrapper intercepts fetch responses and automatically handles token refresh
 * when a 401 Unauthorized response is received. It ensures:
 * 
 * 1. Transparent token refresh - Components don't need to know about token expiry
 * 2. Prevents duplicate refresh calls - Uses a refresh lock
 * 3. Automatic request retry - After successful token refresh, retries the original request
 * 4. Graceful degradation - Falls back to login if refresh fails
 * 
 * Usage:
 *   import { fetchWithTokenRefresh } from './fetchWrapper';
 *   const response = await fetchWithTokenRefresh('/api/activities');
 */

import { ApiResponse, GuestAuthResponse } from './api.js';
import { getAccessToken, getRefreshToken, updateAccessToken, updateBothTokens, clearAuth } from './authStorage.js';

// Prevent duplicate token refresh calls
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

/**
 * Fetch wrapper that automatically handles token refresh on 401 responses
 * 
 * @param url - The URL to fetch
 * @param options - Standard fetch options
 * @returns Fetch Response (with potential token refresh handled transparently)
 */
export async function fetchWithTokenRefresh(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  // Add authorization header if token exists
  const accessToken = getAccessToken();
  const headers = new Headers(options.headers);
  
  if (accessToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  // First attempt with current token
  let response = await fetch(url, {
    ...options,
    headers,
  });

  // If request succeeds, return immediately
  if (response.ok) {
    return response;
  }

  // If 401 (token expired), attempt refresh
  if (response.status === 401 && !isAuthEndpoint(url)) {
    console.log('[fetchWrapper] 401 detected, attempting token refresh...');
    return handleTokenExpiry(url, options);
  }

  // For other errors or auth endpoints, return as-is
  return response;
}

/**
 * Check if the URL is an auth endpoint (to prevent infinite loops)
 */
function isAuthEndpoint(url: string): boolean {
  return url.includes('/auth/refresh') || 
         url.includes('/auth/login') || 
         url.includes('/auth/guest-login');
}

/**
 * Handle token expiry by refreshing and retrying the request
 */
async function handleTokenExpiry(
  url: string,
  options: RequestInit
): Promise<Response> {
  try {
    // If refresh already in progress, wait for it
    if (isRefreshing && refreshPromise) {
      console.log('[fetchWrapper] Refresh already in progress, waiting...');
      const newToken = await refreshPromise;
      return retryRequestWithNewToken(url, options, newToken);
    }

    // Start refresh process
    isRefreshing = true;
    refreshPromise = refreshAccessToken();
    
    const newAccessToken = await refreshPromise;
    
    // Reset refresh state
    isRefreshing = false;
    refreshPromise = null;

    console.log('[fetchWrapper] Token refresh successful, retrying original request');
    return retryRequestWithNewToken(url, options, newAccessToken);
    
  } catch (error) {
    // Reset refresh state
    isRefreshing = false;
    refreshPromise = null;

    console.error('[fetchWrapper] Token refresh failed:', error);
    
    // Clear auth and let app handle redirect
    clearAuth();
    
    throw new Error('Session expired. Please log in again.');
  }
}

/**
 * Retry the original request with the new access token
 */
function retryRequestWithNewToken(
  url: string,
  options: RequestInit,
  newToken: string
): Promise<Response> {
  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${newToken}`);

  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * Refresh the access token using the refresh token
 * 
 * Supports refresh token rotation: If backend returns a new refresh token,
 * both access and refresh tokens are updated. Otherwise, only access token is updated.
 * 
 * NOTE: This will fail until the backend implements /api/auth/refresh endpoint
 * For now, it will throw an error and trigger logout
 */
async function refreshAccessToken(): Promise<string> {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  // Get API URL from environment
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

  console.log('[fetchWrapper] Calling refresh endpoint...');
  
  // Call the refresh endpoint (will be implemented in backend later)
  const response = await fetch(`${API_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    // Get error message from response if available
    let errorMessage = 'Token refresh failed';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch {
      // Ignore JSON parse errors
    }
    
    throw new Error(errorMessage);
  }

  const result: ApiResponse<GuestAuthResponse> = await response.json();
  
  // Extract tokens from response
  // Backend should return: { success: true, data: { accessToken: "...", refreshToken: "..." } }
  const newAccessToken = result.data?.tokens.access
  const newRefreshToken = result.data?.tokens.refresh
  
  if (!newAccessToken) {
    throw new Error('No access token in refresh response');
  }

  // Update stored tokens
  if (newRefreshToken) {
    // Backend supports refresh token rotation - update both tokens
    console.log('[fetchWrapper] Refresh token rotation enabled - updating both tokens');
    updateBothTokens(newAccessToken, newRefreshToken);
  } else {
    // Backend only returns new access token - update access token only
    console.log('[fetchWrapper] Updating access token only (refresh token rotation not enabled)');
    updateAccessToken(newAccessToken);
  }
  
  console.log('[fetchWrapper] Token(s) updated successfully');
  
  return newAccessToken;
}
